import { useMemo } from 'react';
import { useGameState } from './useGameState';
import { buildGradient, getDefaultGradient, getDefaultButtonColor, isValidColor } from '../config/theme';

/**
 * Result of adventure style resolution
 */
interface AdventureStyleResult {
  backgroundGradient: string;
  buttonColor: string;
  source: 'default' | 'adventure' | 'character' | 'scene';
}

/**
 * Hook to resolve the current background gradient and button color based on the style cascade
 * 
 * Style cascade hierarchy: Scene > Character > Adventure > Default
 * 
 * @returns The resolved background gradient, button color, and their source
 */
export function useAdventureStyle(): AdventureStyleResult {
  const gameState = useGameState();
  
  return useMemo(() => {
    // Get style sources from current game state
    const sceneStyle = (gameState.currentScene as any)?.style;
    const characterStyle = (gameState.currentPlayer as any)?.style;
    const adventureStyle = gameState.currentAdventure?.style;
    
    const defaultGradient = getDefaultGradient();
    const defaultButtonColor = getDefaultButtonColor();
    
    // Determine button color (cascade: Scene > Character > Adventure > Default)
    let buttonColor = defaultButtonColor;
    let buttonColorSource: 'default' | 'adventure' | 'character' | 'scene' = 'default';
    
    if (sceneStyle?.buttonColor && isValidColor(sceneStyle.buttonColor)) {
      buttonColor = sceneStyle.buttonColor;
      buttonColorSource = 'scene';
    } else if (characterStyle?.buttonColor && isValidColor(characterStyle.buttonColor)) {
      buttonColor = characterStyle.buttonColor;
      buttonColorSource = 'character';
    } else if (adventureStyle?.buttonColor && isValidColor(adventureStyle.buttonColor)) {
      buttonColor = adventureStyle.buttonColor;
      buttonColorSource = 'adventure';
    }
    
    // Determine background gradient (cascade: Scene > Character > Adventure > Default)
    let backgroundGradient = defaultGradient;
    let gradientSource: 'default' | 'adventure' | 'character' | 'scene' = 'default';
    
    // Try scene gradient first (highest priority)
    if (sceneStyle?.backgroundGradient) {
      try {
        const gradient = buildGradient(sceneStyle.backgroundGradient);
        // Check if buildGradient returned the default (fallback) gradient
        if (gradient !== defaultGradient) {
          backgroundGradient = gradient;
          gradientSource = 'scene';
        }
      } catch (error) {
        console.warn('Failed to build scene gradient, falling back to next level', error);
      }
    }
    
    // Try character gradient if scene didn't work
    if (gradientSource === 'default' && characterStyle?.backgroundGradient) {
      try {
        const gradient = buildGradient(characterStyle.backgroundGradient);
        // Check if buildGradient returned the default (fallback) gradient
        if (gradient !== defaultGradient) {
          backgroundGradient = gradient;
          gradientSource = 'character';
        }
      } catch (error) {
        console.warn('Failed to build character gradient, falling back to next level', error);
      }
    }
    
    // Try adventure gradient if character didn't work
    if (gradientSource === 'default' && adventureStyle?.backgroundGradient) {
      try {
        const gradient = buildGradient(adventureStyle.backgroundGradient);
        // Check if buildGradient returned the default (fallback) gradient
        if (gradient !== defaultGradient) {
          backgroundGradient = gradient;
          gradientSource = 'adventure';
        }
      } catch (error) {
        console.warn('Failed to build adventure gradient, falling back to default', error);
      }
    }
    
    // Use the highest priority source for the result
    // (button color and gradient sources may differ)
    const source = buttonColorSource !== 'default' ? buttonColorSource : gradientSource;
    
    return {
      backgroundGradient,
      buttonColor,
      source
    };
  }, [
    // Re-compute when any of these change
    (gameState.currentScene as any)?.id, // Scene ID as proxy for scene changes
    (gameState.currentScene as any)?.style, // Direct style dependency
    gameState.currentPlayer?.characterName, // Character as proxy for character changes
    gameState.currentAdventure?.title, // Adventure as proxy for adventure changes
    gameState.currentAdventure?.style, // Direct style dependency
  ]);
}

export default useAdventureStyle;

